/**
 * Utilitário para gerenciar WebGL com proteção para mobile
 * Detecta limitações e adiciona fallbacks
 */

// Cache de contextos WebGL para evitar criar múltiplos contextos
const contextCache = new WeakMap();

/**
 * Detecta se é um dispositivo móvel
 */
export function isMobileDevice() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * Verifica se WebGL está disponível e funcional
 */
export function isWebGLAvailable() {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    if (!gl) {
      return false;
    }

    // Verificar se o contexto não está perdido
    return !gl.isContextLost();
  } catch (e) {
    return false;
  }
}

/**
 * Obtém contexto WebGL com tratamento de erro e cache
 */
export function getWebGLContext(canvas, options = {}) {
  // Verificar cache primeiro
  if (contextCache.has(canvas)) {
    const cachedGl = contextCache.get(canvas);
    if (!cachedGl.isContextLost()) {
      return cachedGl;
    }
  }

  const defaultOptions = {
    alpha: true,
    premultipliedAlpha: false,
    preserveDrawingBuffer: true,
    antialias: false, // Desabilitar antialiasing em mobile para performance
    powerPreference: isMobileDevice() ? 'low-power' : 'high-performance',
    failIfMajorPerformanceCaveat: false
  };

  const finalOptions = { ...defaultOptions, ...options };

  try {
    const gl = canvas.getContext('webgl', finalOptions) || 
               canvas.getContext('experimental-webgl', finalOptions);
    
    if (!gl) {
      console.warn('WebGL não suportado');
      return null;
    }

    // Adicionar listener para contexto perdido
    canvas.addEventListener('webglcontextlost', (e) => {
      console.warn('Contexto WebGL perdido', e);
      e.preventDefault();
    }, false);

    // Adicionar listener para contexto restaurado
    canvas.addEventListener('webglcontextrestored', () => {
      console.log('Contexto WebGL restaurado');
    }, false);

    // Cache o contexto
    contextCache.set(canvas, gl);

    return gl;
  } catch (e) {
    console.error('Erro ao obter contexto WebGL:', e);
    return null;
  }
}

/**
 * Limpa recursos WebGL com segurança
 */
export function cleanupWebGLResources(gl, resources = {}) {
  if (!gl || gl.isContextLost()) {
    return;
  }

  const { textures = [], programs = [], shaders = [], buffers = [], framebuffers = [] } = resources;

  try {
    // Deletar texturas
    textures.forEach(texture => {
      if (texture && gl.isTexture(texture)) {
        gl.deleteTexture(texture);
      }
    });

    // Deletar programas
    programs.forEach(program => {
      if (program && gl.isProgram(program)) {
        gl.deleteProgram(program);
      }
    });

    // Deletar shaders
    shaders.forEach(shader => {
      if (shader && gl.isShader(shader)) {
        gl.deleteShader(shader);
      }
    });

    // Deletar buffers
    buffers.forEach(buffer => {
      if (buffer && gl.isBuffer(buffer)) {
        gl.deleteBuffer(buffer);
      }
    });

    // Deletar framebuffers
    framebuffers.forEach(framebuffer => {
      if (framebuffer && gl.isFramebuffer(framebuffer)) {
        gl.deleteFramebuffer(framebuffer);
      }
    });
  } catch (e) {
    console.warn('Erro ao limpar recursos WebGL:', e);
  }
}

/**
 * Compila shader com tratamento de erro
 */
export function compileShader(gl, source, type) {
  if (!gl || gl.isContextLost()) {
    console.error('Contexto WebGL inválido ou perdido');
    return null;
  }

  try {
    const shader = gl.createShader(type);
    
    if (!shader) {
      console.error('Falha ao criar shader');
      return null;
    }

    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const error = gl.getShaderInfoLog(shader);
      console.error('Erro ao compilar shader:', error);
      gl.deleteShader(shader);
      return null;
    }

    return shader;
  } catch (e) {
    console.error('Exceção ao compilar shader:', e);
    return null;
  }
}

/**
 * Cria programa WebGL com tratamento de erro
 */
export function createProgram(gl, vertexShader, fragmentShader) {
  if (!gl || gl.isContextLost()) {
    console.error('Contexto WebGL inválido ou perdido');
    return null;
  }

  try {
    const program = gl.createProgram();
    
    if (!program) {
      console.error('Falha ao criar programa');
      return null;
    }

    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const error = gl.getProgramInfoLog(program);
      console.error('Erro ao linkar programa:', error);
      gl.deleteProgram(program);
      return null;
    }

    return program;
  } catch (e) {
    console.error('Exceção ao criar programa:', e);
    return null;
  }
}

/**
 * Cria textura com tratamento de erro
 */
export function createTexture(gl, image, options = {}) {
  if (!gl || gl.isContextLost()) {
    console.error('Contexto WebGL inválido ou perdido');
    return null;
  }

  const {
    wrapS = gl.CLAMP_TO_EDGE,
    wrapT = gl.CLAMP_TO_EDGE,
    minFilter = gl.LINEAR,
    magFilter = gl.LINEAR
  } = options;

  try {
    const texture = gl.createTexture();
    
    if (!texture) {
      console.error('Falha ao criar textura');
      return null;
    }

    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, wrapS);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, wrapT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, minFilter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, magFilter);

    if (image) {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
    }

    return texture;
  } catch (e) {
    console.error('Exceção ao criar textura:', e);
    return null;
  }
}

/**
 * Verifica limitações de WebGL em mobile
 */
export function getWebGLLimitations(gl) {
  if (!gl) return null;

  try {
    return {
      maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
      maxRenderbufferSize: gl.getParameter(gl.MAX_RENDERBUFFER_SIZE),
      maxViewportDims: gl.getParameter(gl.MAX_VIEWPORT_DIMS),
      maxVertexTextureImageUnits: gl.getParameter(gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS),
      maxTextureImageUnits: gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS),
      maxCombinedTextureImageUnits: gl.getParameter(gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS),
      isMobile: isMobileDevice()
    };
  } catch (e) {
    console.warn('Erro ao obter limitações WebGL:', e);
    return null;
  }
}

/**
 * Redimensiona imagem se exceder limites de WebGL
 */
export function resizeImageForWebGL(image, gl) {
  const maxSize = gl ? gl.getParameter(gl.MAX_TEXTURE_SIZE) : 2048;
  
  // Em mobile, limitar ainda mais para economizar memória
  const effectiveMaxSize = isMobileDevice() ? Math.min(maxSize, 2048) : maxSize;

  if (image.width <= effectiveMaxSize && image.height <= effectiveMaxSize) {
    return image;
  }

  const canvas = document.createElement('canvas');
  const scale = Math.min(effectiveMaxSize / image.width, effectiveMaxSize / image.height);
  
  canvas.width = Math.floor(image.width * scale);
  canvas.height = Math.floor(image.height * scale);

  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  console.warn(`Imagem redimensionada de ${image.width}x${image.height} para ${canvas.width}x${canvas.height} para compatibilidade WebGL`);

  return canvas;
}

